<template>
  <article class="resort-item resort-item-small">
    <search-item-images :resort="resort" />

    <a :href="supplierURL" target="_blank">
      <h4 class="fs-20 font-medium mt-6 flex items-center">
        <span v-html="resort.__platform_name" />
        <booking-term-icon
          v-if="hasBookingTerm"
          :type="bookingTerm.type"
          class="ml-2 fill-blue"
        />
      </h4>

      <p
        class="mt-1 text-black-light-5 fs-14"
        v-html="resort.__platform_country_title"
      ></p>

      <div class="mt-2 flex items-end" v-if="!!resort.rating">
        <rating :value="resort.rating.supplier_rating" class="mr-1" size="28" />
        <span class="fs-14 text-black-light-5"
          >{{ resort.rating.count }}
          {{ resort.rating.count > 1 ? "reviews" : "review" }}</span
        >
      </div>

      <div v-else-if="resort.__platform_rating" class="flex items-center mt-4">
        <img
          :src="tripadvisorImage"
          :alt="resort.__platform_name"
          width="119"
          height="20"
          class="review-image max-w-none"
        />
        <a
          :href="resort.__platform_tripadvisor_url"
          class="review-text underline text-black-light-5 text-sm leading-none"
        >
          <span>{{ resort.__platform_tripadvisor_total_review }} reviews</span>
        </a>
      </div>

      <div v-if="displayingFacilities.length" class="text-black-light-5 mt-4">
        <ul class="list-reset flex items-center flex-wrap">
          <li
            v-for="(item, index) in displayingFacilities"
            :key="index"
            class="mr-2 flex flex-items has-tooltip"
          >
            <span class="tooltip rounded shadow-lg py-1 px-4 fs-16">{{
              item.name
            }}</span>
            <img width="28" height="28" :src="item.icon" :alt="item.name" />
            <span class="ml-1">{{
              item.name === "Internet" ? "Wifi" : item.name
            }}</span>
          </li>
        </ul>
      </div>

      <p class="mt-4">
        {{ resort.__platform_description }}
      </p>

      <div class="flex items-center justify-between my-4">
        <div class="fs-16 leading-tight">
          from<br />
          <span class="fs-20 font-medium">
            <currency-context>
              <template v-slot="{ convert }">
                <span
                  v-text="
                    convert(resort.__odyssey_price_per_day.toLocaleString())
                  "
                ></span>
              </template>
            </currency-context>
          </span>
          /day
        </div>
        <button class="btn btn-primary">View boat</button>
      </div>

      <div
        class="my-4"
        v-if="
          resort.__odyssey_greenfins_membership_level ||
            resort.__odyssey_zublu_featured
        "
      >
        <div
          v-if="resort.__odyssey_greenfins_membership_level !== 'none'"
          class="greenfin-tag mr-2 capitalize"
          :class="`${resort.__odyssey_greenfins_membership_level}-color`"
        >
          Green Fins {{ resort.__odyssey_greenfins_membership_level }} Member
        </div>
        <div v-if="resort.__odyssey_zublu_featured" class="top-pick-tag mr-2">
          Our top pick
        </div>
      </div>

      <ul
        class="list-reset trip-list mt-4 fs-14"
        v-if="tripsForSelectedMonthAndFilters.length"
      >
        <li
          v-for="trip in tripsForSelectedMonthAndFilters"
          :key="trip.id"
          class="trip-item relative"
          :class="getTripClass(trip)"
        >
          <div
            class="w-1/5 flex flex-col items-center justify-between text-center"
          >
            <div class="font-medium text-3xl leading-none">
              {{ formatDate(trip.date).day }}
            </div>
            <div class="text-20 leading-none">
              {{ formatDate(trip.date).month }}
            </div>
          </div>
          <div class="flex-1 flex flex-col items-center justify-between">
            <div class="h-5 flex items-center">
              <svg
                width="20"
                height="20"
                class="inline-block mr-1"
                fill="currentColor"
              >
                <use xlink:href="/img/sprite.svg#ic_calendar"></use>
              </svg>
              {{ trip.length + 1 }}D / {{ trip.length }}N
            </div>
            <div class="h-5 flex mt-2">
              <svg
                width="20"
                height="20"
                class="inline-block mr-1"
                fill="currentColor"
              >
                <use xlink:href="/img/sprite.svg#ic_diving"></use>
              </svg>
              {{
                trip.dive_number === "0"
                  ? "No diving"
                  : `${trip.dive_number} dives`
              }}
            </div>
          </div>
          <div class="flex flex-col items-center justify-between">
            <div class="text-right">
              <div class="relative inline-block">
                <div
                  class="old-price line-through"
                  v-if="hasDiscount(trip) && trip.discount_price"
                >
                  <currency-context>
                    <template v-slot="{ convert }">
                      <span v-text="convert(trip.price.real_price)"></span>
                    </template>
                  </currency-context>
                </div>

                <span class="font-medium leading-5">
                  <currency-context>
                    <template v-slot="{ convert }">
                      <span v-text="convert(trip.price.price)"></span>
                    </template>
                  </currency-context>
                </span>
              </div>
            </div>
            <div
              class="text-right font-medium availablity-text leading-5"
              :class="{ 'mt-2': !hasDiscount(trip) }"
            >
              {{ trip.availability | formatAvailability }}
            </div>
          </div>

          <div class="discount-tag" v-if="hasDiscount(trip)">
            <template
              v-if="
                trip.discount_method === 'fix_amount' && trip.discount_price
              "
            >
              -
              <currency-context>
                <template v-slot="{ convert }">
                  <span v-text="convert(trip.discount_price)"></span>
                </template>
              </currency-context>
            </template>
            <template
              v-else-if="
                trip.discount_method === 'percentage' && trip.discount_price
              "
            >
              {{ trip.discount_price }}% off
            </template>
            <template v-else-if="trip.discount_method === 'custom'">
              Special rate
            </template>
          </div>
        </li>
      </ul>
    </a>
  </article>
</template>

<script>
import Rating from "../core/Rating";
import LiveaboardItemMixin from "../../mixins/LiveaboardItemMixin";
import SearchItemImages from "./SearchItemImages";
import BookingTermIcon from "./mixins/BookingTermIcon";
import CurrencyContext from "../core/CurrencyContext";

export default {
  mixins: [LiveaboardItemMixin],
  components: { BookingTermIcon, SearchItemImages, Rating, CurrencyContext },
  props: ["resort"],
  computed: {
    hasBookingTerm() {
      return typeof this.resort.__odyssey_booking_terms === "object";
    },
    bookingTerm() {
      return this.hasBookingTerm ? this.resort.__odyssey_booking_terms : {};
    },
  },
  methods: {
    formatDate(date) {
      const fullDate = moment(date, "YYYY-MM-DD");
      const day = fullDate.format("DD");
      const month = fullDate.format("MMM");
      return { day, month };
    },
  },
};
</script>

<style scoped>
.resort-image {
  min-height: 190px;
}

.resort-item {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
}

.review-text {
  position: relative;
  line-height: 3px;
}

.trip-item {
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid var(--black-light-2);
  border-right: 1px solid var(--black-light-2);
  border-left: 1px solid var(--black-light-2);
}

.trip-item:last-child {
  border-bottom: 1px solid var(--black-light-2);
  border-bottom-left-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
}

.trip-item:first-child {
  border-top: 1px solid var(--black-light-2);
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
}

.trip-item.trip-discounted {
  border-color: var(--discount-primary) !important;
  background-color: var(--discount-basis-bg) !important;
  padding-top: 24px;
  padding-bottom: 24px;
}

.trip-item:not(.trip-discounted) + .trip-item.trip-discounted {
  border-top: 1px solid var(--discount-primary);
}

.trip-item:nth-child(2n + 1) {
  background-color: var(--black-light-1);
}

.trip-item.trip-discounted .discount-tag {
  white-space: nowrap;
  position: absolute;
  background-color: var(--discount-primary);
  color: white;
  font-size: 12px;
  right: 0;
  top: 0;
  border-top-right-radius: 4px;
  border-bottom-left-radius: 4px;
  line-height: 16px;
  padding: 1px 10px;
}

.trip-item:not(:first-child) .discount-tag {
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
  border-top-left-radius: 0px;
}

.trip-item.trip-discounted .price-box {
  top: 16px;
}

.old-price {
  line-height: 16px;
  font-size: 12px;
  color: var(--black-light-5);
}
</style>
